import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Alert,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Add, Edit, Delete, Refresh, LockClock } from '@mui/icons-material';
import SalaryAdvanceHeader from './SalaryAdvanceHeader';
import api, { salaryAdvanceAPI, employeeAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const THIRTY_ONE_DAYS_MS = 31 * 24 * 60 * 60 * 1000;

// Calendar-only approximation of the current pay month (previous calendar
// month) - used strictly as a same-render fallback until the real,
// closure-aware value loads from /salary-advance/current-month below, never
// as the final answer: it doesn't know whether that month's Salary Advance
// has actually been closed yet, so right after closing e.g. August it would
// still say August instead of advancing to September.
function calendarFallbackMonth() {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { month: d.getMonth() + 1, year: d.getFullYear(), label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}` };
}

const emptyNormalForm = { emp_id: '', amount: '', advance_month: '', advance_year: '' };
const emptyEmiForm = {
  emp_id: '', amount: '', monthly_deduction: '', balance_amount: '',
  // "Change Deduction This Month" - only meaningful once the EMI already
  // exists (openEditEmi populates these from the record; a brand new EMI
  // being added has nothing to override yet, see the dialog below).
  overrideChecked: false, overrideAmount: '',
};

function SalaryAdvancePage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('salary_advance.manage');
  const canViewReports = hasPermission('salary_advance.reports');
  const canClose = hasPermission('salary_advance.close');

  const TABS = [
    canManage && { key: 'manage', label: 'ADD ADVANCE' },
    canViewReports && { key: 'reports', label: 'Reports' },
    canClose && { key: 'close', label: 'Close Advance' },
  ].filter(Boolean);

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const activeTab = TABS[activeTabIndex]?.key;

  const [employees, setEmployees] = useState([]);
  const [normalAdvances, setNormalAdvances] = useState([]);
  const [emiAdvances, setEmiAdvances] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Normal Advance's month picker is locked to a single value: the next
  // unclosed month, per the backend's actual Close Advance state - not
  // calendar math, so closing August's Salary Advance immediately makes
  // September the new locked value here too, without waiting for the
  // calendar to roll over. calendarFallbackMonth() is only the same-render
  // placeholder until this loads.
  const [defaultMonthOption, setDefaultMonthOption] = useState(calendarFallbackMonth);
  const monthOptions = useMemo(() => [defaultMonthOption], [defaultMonthOption]);

  const loadCurrentAdvanceMonth = async () => {
    try {
      const response = await salaryAdvanceAPI.getCurrentMonth();
      setDefaultMonthOption(response.data);
    } catch (err) {
      // Keep the calendar-fallback value already in state.
    }
  };

  // Normal Advance dialog state
  const [normalOpen, setNormalOpen] = useState(false);
  const [normalEditing, setNormalEditing] = useState(null);
  const [normalForm, setNormalForm] = useState(emptyNormalForm);
  const [salaryEligible, setSalaryEligible] = useState(null);
  const [salaryEligibleLoading, setSalaryEligibleLoading] = useState(false);

  // EMI Advance dialog state
  const [emiOpen, setEmiOpen] = useState(false);
  const [emiEditing, setEmiEditing] = useState(null);
  const [emiForm, setEmiForm] = useState(emptyEmiForm);

  // Report state - defaults to the previous month, not the current one
  const now = new Date();
  const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [reportMonth, setReportMonth] = useState(prevMonthDate.getMonth() + 1);
  const [reportYear, setReportYear] = useState(prevMonthDate.getFullYear());
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');

  // Close Advance tab state - the picker only ever offers the next unclosed
  // month plus the one after it (see /payroll/close-available-months), not a
  // free-form month/year pick, so old already-processed months can never be
  // selected. closeMonth/closeYear default to whatever that endpoint's first
  // option is once it loads.
  const [closeMonthOptions, setCloseMonthOptions] = useState([]);
  const [closeMonth, setCloseMonth] = useState(prevMonthDate.getMonth() + 1);
  const [closeYear, setCloseYear] = useState(prevMonthDate.getFullYear());
  const [isClosed, setIsClosed] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [closeMessage, setCloseMessage] = useState('');
  const [closeError, setCloseError] = useState('');

  const loadAll = async () => {
    try {
      const [empRes, normalRes, emiRes] = await Promise.all([
        employeeAPI.getLookup(),
        salaryAdvanceAPI.getNormal(),
        salaryAdvanceAPI.getEmi(),
      ]);
      setEmployees(empRes.data);
      setNormalAdvances(normalRes.data);
      setEmiAdvances(emiRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load');
    }
  };

  useEffect(() => {
    loadAll();
    loadCurrentAdvanceMonth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReport = async (month, year) => {
    setReportLoading(true);
    setReportError('');
    try {
      const response = await salaryAdvanceAPI.getReport(month, year);
      setReportData(response.data);
    } catch (err) {
      setReportError(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setReportLoading(false);
    }
  };

  // Only fetches while the Reports tab is actually open - this report's
  // Section 2 loops over every active EMI loan doing 2 queries each to
  // rebuild its full history, so running it unconditionally on every page
  // load (regardless of which tab is open) was slowing down the whole page,
  // not just Reports.
  useEffect(() => {
    if (activeTab !== 'reports') return;
    loadReport(reportMonth, reportYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, reportMonth, reportYear]);

  // ==================== CLOSE ADVANCE ====================
  const loadCloseMonthOptions = async () => {
    try {
      const response = await api.get('/payroll/close-available-months');
      const opts = response.data.months;
      setCloseMonthOptions(opts);
      if (opts.length > 0) {
        setCloseMonth(opts[0].month);
        setCloseYear(opts[0].year);
      }
    } catch (err) {
      setCloseMonthOptions([]);
    }
  };

  useEffect(() => {
    if (activeTab !== 'close') return;
    loadCloseMonthOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'close') return;
    setCloseMessage('');
    setCloseError('');
    api.get('/payroll/close-status', { params: { month: closeMonth, year: closeYear } })
      .then((response) => setIsClosed(response.data.closed))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, closeMonth, closeYear]);

  const handleClosePayroll = async () => {
    if (!window.confirm(
      `Close Salary Advance for ${MONTH_NAMES[closeMonth - 1]} ${closeYear}? This decrements every ` +
      `active EMI advance's balance and records this month's installments - Upload Attendance will show a ` +
      `warning if next month's data is uploaded before this is closed, and this month's Normal Advances will ` +
      `stop showing on the Salary Advance tab (still visible via Reports).`
    )) return;

    setIsClosing(true);
    setCloseMessage('');
    setCloseError('');
    try {
      const response = await api.post('/payroll/close', null, { params: { month: closeMonth, year: closeYear } });
      setCloseMessage(response.data.message);
      setIsClosed(true);
      loadAll();
      loadCloseMonthOptions();
      loadCurrentAdvanceMonth();
    } catch (err) {
      setCloseError(err.response?.data?.detail || 'Failed to close Salary Advance');
    } finally {
      setIsClosing(false);
    }
  };

  const flash = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  const empName = (empId) => employees.find((e) => e.emp_id === empId)?.emp_name || empId;

  // ==================== NORMAL ADVANCE ====================
  const openAddNormal = () => {
    setNormalEditing(null);
    setNormalForm({ ...emptyNormalForm, advance_month: defaultMonthOption.month, advance_year: defaultMonthOption.year });
    setSalaryEligible(null);
    setError('');
    setNormalOpen(true);
  };

  const openEditNormal = (advance) => {
    setNormalEditing(advance);
    setNormalForm({
      emp_id: advance.emp_id,
      amount: advance.amount,
      advance_month: advance.advance_month,
      advance_year: advance.advance_year,
    });
    setSalaryEligible(null);
    setError('');
    setNormalOpen(true);
  };

  const closeNormal = () => {
    setNormalOpen(false);
    setNormalForm(emptyNormalForm);
    setSalaryEligible(null);
  };

  // Fetch Salary Eligible whenever both Employee and Month are set.
  useEffect(() => {
    if (!normalOpen || !normalForm.emp_id || !normalForm.advance_month || !normalForm.advance_year) {
      setSalaryEligible(null);
      return;
    }
    let cancelled = false;
    setSalaryEligibleLoading(true);
    salaryAdvanceAPI
      .getSalaryEligible(normalForm.emp_id, normalForm.advance_month, normalForm.advance_year)
      .then((res) => {
        if (!cancelled) setSalaryEligible(res.data.salary_eligible);
      })
      .catch(() => {
        if (!cancelled) setSalaryEligible(null);
      })
      .finally(() => {
        if (!cancelled) setSalaryEligibleLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [normalOpen, normalForm.emp_id, normalForm.advance_month, normalForm.advance_year]);

  const handleSaveNormal = async () => {
    if (!normalForm.emp_id || !normalForm.amount || !normalForm.advance_month || !normalForm.advance_year) {
      setError('Employee, Amount, and Month are all required');
      return;
    }
    try {
      const payload = {
        emp_id: normalForm.emp_id,
        amount: parseFloat(normalForm.amount),
        advance_month: normalForm.advance_month,
        advance_year: normalForm.advance_year,
      };
      if (normalEditing) {
        await salaryAdvanceAPI.updateNormal(normalEditing.id, payload);
        flash('Normal Advance updated');
      } else {
        await salaryAdvanceAPI.createNormal(payload);
        flash('Normal Advance added');
      }
      closeNormal();
      loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save Normal Advance');
    }
  };

  const handleDeleteNormal = async (advance) => {
    if (!window.confirm(`Delete this Normal Advance for ${empName(advance.emp_id)}?`)) return;
    try {
      await salaryAdvanceAPI.deleteNormal(advance.id);
      flash('Normal Advance deleted');
      loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete Normal Advance');
    }
  };

  // ==================== EMI ADVANCE ====================
  const openAddEmi = () => {
    setEmiEditing(null);
    setEmiForm(emptyEmiForm);
    setError('');
    setEmiOpen(true);
  };

  const openEditEmi = (advance) => {
    setEmiEditing(advance);
    const hasOverride = advance.next_installment_override_amount !== null
      && advance.next_installment_override_amount !== undefined;
    setEmiForm({
      emp_id: advance.emp_id,
      amount: advance.amount,
      monthly_deduction: advance.monthly_deduction,
      balance_amount: advance.balance_amount,
      overrideChecked: hasOverride,
      overrideAmount: hasOverride ? String(advance.next_installment_override_amount) : '',
    });
    setError('');
    setEmiOpen(true);
  };

  const closeEmi = () => {
    setEmiOpen(false);
    setEmiForm(emptyEmiForm);
  };

  const handleSaveEmi = async () => {
    if (!emiForm.emp_id || !emiForm.amount || !emiForm.monthly_deduction || emiForm.balance_amount === '') {
      setError('Employee, Advance Amount, Monthly Deduction, and Balance Amount are all required');
      return;
    }
    if (emiForm.overrideChecked && emiForm.overrideAmount === '') {
      setError('Enter an amount for Change Deduction This Month (0 to skip, or any other amount)');
      return;
    }
    try {
      const payload = {
        emp_id: emiForm.emp_id,
        amount: parseFloat(emiForm.amount),
        monthly_deduction: parseFloat(emiForm.monthly_deduction),
        balance_amount: parseFloat(emiForm.balance_amount),
      };
      if (emiEditing) {
        await salaryAdvanceAPI.updateEmi(emiEditing.id, payload);
        await salaryAdvanceAPI.setEmiNextDeductionOverride(
          emiEditing.id,
          emiForm.overrideChecked ? parseFloat(emiForm.overrideAmount) : null
        );
        flash('EMI Advance updated');
      } else {
        await salaryAdvanceAPI.createEmi(payload);
        flash('EMI Advance added');
      }
      closeEmi();
      loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save EMI Advance');
    }
  };

  const handleDeleteEmi = async (advance) => {
    if (!window.confirm(`Delete this EMI Advance for ${empName(advance.emp_id)}?`)) return;
    try {
      await salaryAdvanceAPI.deleteEmi(advance.id);
      flash('EMI Advance deleted');
      loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete EMI Advance');
    }
  };

  // Normal Advances list shows only recently-added records - the last 31
  // days by created_at (when the advance was actually entered), not which
  // pay month it's tagged for. Replaces the old Month filter dropdown: a
  // rolling window needs no picker and never goes stale.
  const filteredNormalAdvances = useMemo(() => {
    const cutoff = Date.now() - THIRTY_ONE_DAYS_MS;
    return normalAdvances.filter((a) => new Date(a.created_at).getTime() >= cutoff);
  }, [normalAdvances]);

  return (
    <Box>
      <SalaryAdvanceHeader />
      <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
        {TABS.length > 1 && (
          <Tabs
            value={activeTabIndex}
            onChange={(e, newValue) => setActiveTabIndex(newValue)}
            sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
          >
            {TABS.map((tab) => (
              <Tab key={tab.key} label={tab.label} />
            ))}
          </Tabs>
        )}

        {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
        {error && !normalOpen && !emiOpen && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {!activeTab && (
          <Alert severity="info">You don't have access to any Salary Advance sections.</Alert>
        )}

        {activeTab === 'manage' && (
          <>
        {/* Normal Advances */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6">Normal Advances</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={openAddNormal}>Add</Button>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              A one-time advance amount for a specific pay cycle. Showing advances added in the last 31 days.
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Advance for Month</TableCell>
                    <TableCell align="right" sx={{ width: 90 }}>Amount</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredNormalAdvances.length === 0 ? (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>No Normal Advances in the last 31 days</TableCell></TableRow>
                  ) : filteredNormalAdvances.map((a) => (
                    <TableRow key={a.id} hover>
                      <TableCell>{empName(a.emp_id)}</TableCell>
                      <TableCell>{MONTH_NAMES[a.advance_month - 1]} {a.advance_year}</TableCell>
                      <TableCell align="right">₹{a.amount}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEditNormal(a)}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteNormal(a)}><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredNormalAdvances.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        ₹{filteredNormalAdvances.reduce((sum, a) => sum + Number(a.amount || 0), 0)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* EMI Advances */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">EMI Advances</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={openAddEmi}>Add</Button>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              A running loan-style balance, deducted every pay cycle. This month deduction is not yet included.
              Balances are decremented from the Payroll module's when "Close Payroll" action is performed.
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" sx={{ width: 'auto' }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Month</TableCell>
                    <TableCell align="right">Advance Amount</TableCell>
                    <TableCell align="right">Monthly Deduction</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell sx={{ maxWidth: 110 }}>Change Deduction This Month</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emiAdvances.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ color: 'text.secondary' }}>No EMI Advances yet</TableCell></TableRow>
                  ) : emiAdvances.map((a) => {
                    const isActive = Number(a.balance_amount) > 0;
                    const hasOverride = a.next_installment_override_amount !== null && a.next_installment_override_amount !== undefined;
                    return (
                    <TableRow key={a.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{empName(a.emp_id)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{MONTH_ABBR[new Date(a.created_at).getMonth()]} {new Date(a.created_at).getFullYear()}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>₹{a.amount}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>₹{a.monthly_deduction}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>₹{a.balance_amount}</TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Chip
                          size="small"
                          label={isActive ? 'Active' : 'Completed'}
                          color={isActive ? 'primary' : 'default'}
                        />
                      </TableCell>
                      {/* Read-only - only changeable via the Edit dialog (pencil icon).
                          The month shown is the current pay month (defaultMonthOption)
                          - the same cycle this override actually applies to. */}
                      <TableCell>
                        {isActive && hasOverride ? (
                          <Chip
                            size="small"
                            label={Number(a.next_installment_override_amount) === 0
                              ? `Skipping ${MONTH_ABBR[defaultMonthOption.month - 1]}`
                              : `${MONTH_ABBR[defaultMonthOption.month - 1]} Amt: ₹${a.next_installment_override_amount}`}
                            color="warning"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">{isActive ? 'No Change' : '—'}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <IconButton size="small" onClick={() => openEditEmi(a)}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteEmi(a)}><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  );})}
                  {emiAdvances.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        ₹{emiAdvances.reduce((sum, a) => sum + Number(a.monthly_deduction || 0), 0)}
                      </TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
          </>
        )}

        {activeTab === 'reports' && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6">Salary Advance Report</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Month</InputLabel>
                  <Select label="Month" value={reportMonth} onChange={(e) => setReportMonth(e.target.value)}>
                    {MONTH_NAMES.map((m, idx) => (
                      <MenuItem key={m} value={idx + 1}>{m}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Year</InputLabel>
                  <Select label="Year" value={reportYear} onChange={(e) => setReportYear(e.target.value)}>
                    {[now.getFullYear(), now.getFullYear() - 1].map((y) => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton onClick={() => loadReport(reportMonth, reportYear)} title="Refresh">
                  <Refresh />
                </IconButton>
              </Box>
            </Box>

            {reportError && <Alert severity="error" sx={{ mb: 2 }}>{reportError}</Alert>}
            {reportLoading && <Typography variant="body2" color="textSecondary">Loading...</Typography>}

            {!reportLoading && reportData && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
                  Section 1 — Salary Advance Details This Month
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, border: '2px solid', borderColor: 'text.primary' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Advance Month</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Created Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.section1.length === 0 ? (
                        <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>No Normal Advance activity this month</TableCell></TableRow>
                      ) : reportData.section1.map((row, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>{row.employee}</TableCell>
                          <TableCell>
                            <Chip size="small" label={row.type} color="warning" />
                          </TableCell>
                          <TableCell>{row.advance_month || '—'}</TableCell>
                          <TableCell align="right">₹{row.amount}</TableCell>
                          <TableCell>{row.created_by || '—'}</TableCell>
                          <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</TableCell>
                        </TableRow>
                      ))}
                      {reportData.section1.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={3} sx={{ fontWeight: 700 }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>₹{reportData.section1_total}</TableCell>
                          <TableCell colSpan={2} />
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Section 2 — EMI Breakdown (Full History)
                </Typography>
                {reportData.section2.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">No EMI activity</Typography>
                ) : reportData.section2.map((block) => (
                  <Card key={block.emp_id} variant="outlined" sx={{ mb: 2, border: '2px solid', borderColor: 'text.primary' }}>
                    <Box sx={{ p: 1.5, bgcolor: 'action.hover', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="subtitle2">{block.employee}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        Started: {MONTH_NAMES[block.started_month - 1]} {block.started_year} &nbsp;·&nbsp; Monthly Deduction: ₹{block.monthly_deduction} &nbsp;·&nbsp; Advance Taken: ₹{block.advance_taken}
                      </Typography>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Month</TableCell>
                            <TableCell>Installment Number</TableCell>
                            <TableCell align="right">Deducted Amount</TableCell>
                            <TableCell align="right">Balance Remaining</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {block.months.length === 0 ? (
                            <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>No installments collected yet</TableCell></TableRow>
                          ) : block.months.map((m, idx) => (
                            <TableRow key={idx} sx={m.missed ? { bgcolor: '#fdecea' } : m.is_override ? { bgcolor: '#fff8e1' } : undefined}>
                              <TableCell>{MONTH_NAMES[m.month - 1]} {m.year}</TableCell>
                              <TableCell>
                                {m.is_sanction
                                  ? 'Amount Sanctioned'
                                  : m.missed
                                    ? (m.installment_number
                                        ? <Chip size="small" color="warning" variant="outlined" label={`Installment - ${m.installment_number} - Not Processed Yet`} />
                                        : <Chip size="small" color="error" variant="outlined" label="Missed" />)
                                    : m.is_override
                                      ? (Number(m.deducted_amount) === 0
                                          ? <Chip size="small" color="warning" label={`Installment - ${m.installment_number} - Skipped`} />
                                          : <Chip size="small" color="warning" label={`Installment - ${m.installment_number} - Custom Amount`} />)
                                      : `Installment - ${m.installment_number}`}
                              </TableCell>
                              <TableCell align="right">
                                {m.is_sanction
                                  ? '-'
                                  : m.missed
                                    ? '—'
                                    : m.is_override && Number(m.deducted_amount) === 0
                                      ? 'Skipped'
                                      : `₹${m.deducted_amount}`}
                              </TableCell>
                              <TableCell align="right">₹{m.balance_after}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow>
                            <TableCell colSpan={3} sx={{ fontWeight: 700 }}>Balance to be paid till now</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>₹{block.balance_remaining}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Card>
                ))}
              </>
            )}
          </CardContent>
        </Card>
        )}

        {activeTab === 'close' && (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Close Advance</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Closing a month decrements every active EMI advance's balance and records that cycle's
                installments. It also permanently removes that month's Normal Advances from the working
                Salary Advance tab (still fully visible via Reports). This action is admin-controlled and
                cannot be undone. Only the next unclosed month (and the one after it) can be selected below -
                older, already-processed months are never offered.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
                <FormControl size="small" sx={{ minWidth: 200 }} disabled={closeMonthOptions.length === 0}>
                  <InputLabel>Month</InputLabel>
                  <Select
                    label="Month"
                    value={closeMonthOptions.length > 0 ? `${closeMonth}-${closeYear}` : ''}
                    onChange={(e) => {
                      const [m, y] = e.target.value.split('-').map(Number);
                      setCloseMonth(m);
                      setCloseYear(y);
                    }}
                  >
                    {closeMonthOptions.map((opt) => (
                      <MenuItem key={`${opt.month}-${opt.year}`} value={`${opt.month}-${opt.year}`}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color={isClosed ? 'inherit' : 'error'}
                  startIcon={<LockClock />}
                  onClick={handleClosePayroll}
                  disabled={isClosing || isClosed}
                >
                  {isClosed
                    ? `Closed - ${MONTH_NAMES[closeMonth - 1]} ${closeYear}`
                    : isClosing ? 'Closing...' : 'Close Salary Advance'}
                </Button>
              </Box>

              {closeMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCloseMessage('')}>{closeMessage}</Alert>}
              {closeError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCloseError('')}>{closeError}</Alert>}
            </CardContent>
          </Card>
        )}
      </Container>

      {/* Normal Advance dialog */}
      <Dialog open={normalOpen} onClose={closeNormal} maxWidth="xs" fullWidth>
        <DialogTitle>{normalEditing ? 'Edit Normal Advance' : 'Add Normal Advance'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Employee</InputLabel>
            <Select
              label="Employee"
              value={normalForm.emp_id}
              onChange={(e) => setNormalForm({ ...normalForm, emp_id: e.target.value })}
            >
              {employees.map((emp) => (
                <MenuItem key={emp.emp_id} value={emp.emp_id}>{emp.emp_name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Advance for Month</InputLabel>
            <Select
              label="Advance for Month"
              // Locked to the current pay month - nothing to pick when
              // adding a new advance. Editing an older advance (from before
              // this lock existed) still shows its own original month
              // correctly, via the extra option added below, but that month
              // isn't itself changeable here.
              disabled={!normalEditing}
              value={normalForm.advance_month ? `${normalForm.advance_month}-${normalForm.advance_year}` : ''}
              onChange={(e) => {
                const [m, y] = e.target.value.split('-').map(Number);
                setNormalForm({ ...normalForm, advance_month: m, advance_year: y });
              }}
            >
              {monthOptions.some((opt) => opt.month === normalForm.advance_month && opt.year === normalForm.advance_year)
                ? null
                : normalForm.advance_month && (
                    <MenuItem value={`${normalForm.advance_month}-${normalForm.advance_year}`}>
                      {MONTH_NAMES[normalForm.advance_month - 1]} {normalForm.advance_year}
                    </MenuItem>
                  )}
              {monthOptions.map((opt) => (
                <MenuItem key={`${opt.month}-${opt.year}`} value={`${opt.month}-${opt.year}`}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {normalForm.emp_id && normalForm.advance_month && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {salaryEligibleLoading
                ? 'Loading salary eligible...'
                : salaryEligible !== null
                  ? (typeof salaryEligible === 'string'
                      ? `Salary Eligible: ${salaryEligible}`
                      : `Salary Eligible: ₹${salaryEligible}`)
                  : 'Salary Eligible: not available (no salary record or attendance for this month)'}
            </Alert>
          )}

          <TextField
            fullWidth
            type="number"
            label="Amount"
            value={normalForm.amount}
            onChange={(e) => setNormalForm({ ...normalForm, amount: e.target.value })}
            inputProps={{ min: 0 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeNormal}>Cancel</Button>
          <Button onClick={handleSaveNormal} variant="contained">{normalEditing ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      {/* EMI Advance dialog */}
      <Dialog open={emiOpen} onClose={closeEmi} maxWidth="xs" fullWidth>
        <DialogTitle>{emiEditing ? 'Edit EMI Advance' : 'Add EMI Advance'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <FormControl fullWidth disabled={!!emiEditing} sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Employee</InputLabel>
            <Select
              label="Employee"
              value={emiForm.emp_id}
              onChange={(e) => setEmiForm({ ...emiForm, emp_id: e.target.value })}
            >
              {employees.map((emp) => (
                <MenuItem key={emp.emp_id} value={emp.emp_id}>{emp.emp_name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            type="number"
            label="Advance Amount"
            value={emiForm.amount}
            onChange={(e) => setEmiForm({ ...emiForm, amount: e.target.value })}
            inputProps={{ min: 0 }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            type="number"
            label="Monthly Deduction"
            value={emiForm.monthly_deduction}
            onChange={(e) => setEmiForm({ ...emiForm, monthly_deduction: e.target.value })}
            inputProps={{ min: 0 }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            type="number"
            label="Balance Amount"
            value={emiForm.balance_amount}
            onChange={(e) => setEmiForm({ ...emiForm, balance_amount: e.target.value })}
            inputProps={{ min: 0 }}
          />

          {/* Only meaningful for an EMI that already exists - nothing to
              override yet on a brand new one. */}
          {emiEditing && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={emiForm.overrideChecked}
                    onChange={(e) => setEmiForm({
                      ...emiForm,
                      overrideChecked: e.target.checked,
                      overrideAmount: e.target.checked ? emiForm.overrideAmount : '',
                    })}
                  />
                }
                label="Change Deduction This Month"
              />
              {emiForm.overrideChecked && (
                <TextField
                  fullWidth
                  type="number"
                  label="Amount"
                  value={emiForm.overrideAmount}
                  onChange={(e) => setEmiForm({ ...emiForm, overrideAmount: e.target.value })}
                  helperText="0 = skip, or enter a different amount"
                  inputProps={{ min: 0, step: 0.5 }}
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEmi}>Cancel</Button>
          <Button onClick={handleSaveEmi} variant="contained">{emiEditing ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SalaryAdvancePage;
